import React, { createContext, useCallback, useContext, useMemo, useReducer, useRef } from 'react';
import { logger } from '../lib/terminalLogger';

// ---------------------------------------------
// Tipos e utilitários de atalhos
// ---------------------------------------------

export type KeyboardModifier = 'Control' | 'Shift' | 'Alt' | 'Meta';
export type KeyboardKey = string; // ex.: 'R', '1', 'F2', 'Home'
export type KeyCombo = ReadonlyArray<KeyboardModifier | KeyboardKey>; // ordem não importa para modificadores

export type ToolId = string; // ex.: 'oneregua', 'onebitmapv1'
export type ActionId = string; // ex.: 'convert_to_bitmap', 'Measure.AutoDimensions'

export interface ShortcutBinding {
  toolId: ToolId;
  actionId: ActionId;
  keys: KeyCombo; // ['Control', 'Shift', 'R']
}

export interface ShortcutConflict {
  existing: ShortcutBinding;
  incoming: ShortcutBinding;
}

type ShortcutState = Record<ToolId, Record<ActionId, KeyCombo>>;

type Executor = (context?: unknown) => void | Promise<void>;

// Serialização canônica: 'Control+Shift+R'
export function serializeKeyCombo(keys: KeyCombo): string {
  const mods: KeyboardModifier[] = [];
  let main: string | null = null;
  for (const k of keys) {
    if (k === 'Control' || k === 'Shift' || k === 'Alt' || k === 'Meta') mods.push(k);
    else main = k;
  }
  const ordered = [...mods.sort(), ...(main ? [main] : [])];
  return ordered.join('+');
}

export function normalizeKeyName(key: string): string {
  const map: Record<string, string> = {
    ctrl: 'Control', control: 'Control',
    shift: 'Shift',
    alt: 'Alt',
    meta: 'Meta', command: 'Meta', cmd: 'Meta',
    esc: 'Escape',
    del: 'Delete', bksp: 'Backspace',
  };
  const up = String(key || '').trim();
  const lower = up.toLowerCase();
  if (map[lower]) return map[lower];
  // F-keys
  if (/^f\d{1,2}$/i.test(up)) return up.toUpperCase();
  // Digitos
  if (/^\d$/.test(lower)) return lower;
  // Home/End/etc
  const specials = ['Home','End','Insert','Delete','PageUp','PageDown','ArrowUp','ArrowDown','ArrowLeft','ArrowRight','Escape','Enter','Tab','Space'];
  if (specials.includes(up)) return up;
  // Letras
  if (/^[a-z]$/i.test(lower)) return lower.toUpperCase();
  return up; // fallback
}

/**
 * Normaliza código de tecla do Numpad para formato do backend
 * Numpad5 → Num5, NumpadMultiply → Num*, etc.
 */
function normalizeNumpadCode(code: string): string | null {
  // Numpad números: Numpad0-Numpad9 → Num0-Num9
  const numMatch = code.match(/^Numpad(\d)$/);
  if (numMatch) return `Num${numMatch[1]}`;
  
  // Numpad operadores
  const numpadOps: Record<string, string> = {
    'NumpadMultiply': 'Num*',
    'NumpadAdd': 'Num+',
    'NumpadSubtract': 'Num-',
    'NumpadDivide': 'Num/',
    'NumpadDecimal': 'Num.',
    'NumLock': 'NumLock',
  };
  
  return numpadOps[code] || null;
}

export function eventToKeyCombo(e: KeyboardEvent): KeyCombo {
  const mods: KeyboardModifier[] = [];
  if (e.ctrlKey) mods.push('Control');
  if (e.shiftKey) mods.push('Shift');
  if (e.altKey) mods.push('Alt');
  if (e.metaKey) mods.push('Meta');
  
  // Verificar se é tecla do Numpad usando e.code
  const numpadKey = normalizeNumpadCode(e.code);
  const key = numpadKey || normalizeKeyName(e.key);
  
  // Ignorar teclas puramente modificadoras
  const isModifierOnly = key === 'Control' || key === 'Shift' || key === 'Alt' || key === 'Meta';
  const parts: (KeyboardModifier | KeyboardKey)[] = [...mods];
  if (!isModifierOnly) parts.push(key);
  return parts as KeyCombo;
}

// ---------------------------------------------
// Nota: Persistência em localStorage foi removida
// Atalhos agora vêm exclusivamente da API (hydrate)
// ---------------------------------------------

// ---------------------------------------------
// Contexto
// ---------------------------------------------

type Action =
  | { type: 'set'; toolId: ToolId; actionId: ActionId; keys: KeyCombo }
  | { type: 'remove'; toolId: ToolId; actionId: ActionId }
  | { type: 'hydrate'; state: ShortcutState };

function reducer(state: ShortcutState, action: Action): ShortcutState {
  switch (action.type) {
    case 'hydrate':
      // Carrega estado completo da API (sem persistir em localStorage)
      return action.state;
    case 'set': {
      // @deprecated - usuário não edita mais atalhos
      const next: ShortcutState = { ...state, [action.toolId]: { ...(state[action.toolId] || {}) } };
      next[action.toolId][action.actionId] = [...action.keys];
      // NÃO persiste mais em localStorage - atalhos vêm apenas da API
      return next;
    }
    case 'remove': {
      // @deprecated - usuário não edita mais atalhos
      const next: ShortcutState = { ...state, [action.toolId]: { ...(state[action.toolId] || {}) } };
      delete next[action.toolId][action.actionId];
      if (Object.keys(next[action.toolId]).length === 0) delete next[action.toolId];
      // NÃO persiste mais em localStorage - atalhos vêm apenas da API
      return next;
    }
    default:
      return state;
  }
}

interface KeyboardContextValue {
  state: ShortcutState;
  /** @deprecated Usuário não edita mais atalhos - use hydrate para carregar defaults */
  setShortcut: (binding: ShortcutBinding) => { conflict?: ShortcutConflict };
  /** @deprecated Usuário não edita mais atalhos */
  removeShortcut: (toolId: ToolId, actionId: ActionId) => void;
  listBindings: () => ShortcutBinding[];
  // Execução
  registerExecutor: (toolId: ToolId, actionId: ActionId, executor: Executor) => void;
  unregisterExecutor: (toolId: ToolId, actionId: ActionId) => void;
  execute: (toolId: ToolId, actionId: ActionId, context?: unknown) => void;
  // Resolução por combinação
  findBySerializedCombo: (serialized: string) => ShortcutBinding[];
  /** Carrega atalhos de fonte externa (API) sem persistir em localStorage */
  hydrate: (state: ShortcutState) => void;
  /** Indica se os atalhos foram hidratados (carregados da API) */
  isHydrated: boolean;
}

const KeyboardContext = createContext<KeyboardContextValue | null>(null);

export const KeyboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Inicializa vazio - atalhos serão carregados via hydrate da API
  const [state, dispatch] = useReducer(reducer, {});
  const [isHydrated, setIsHydrated] = React.useState(false);

  // Registro de executores por chave tool:action
  const executorsRef = useRef<Map<string, Executor>>(new Map());

  const makeKey = (toolId: ToolId, actionId: ActionId) => `${toolId}::${actionId}`;

  const setShortcut = useCallback((binding: ShortcutBinding) => {
    // Verificar conflito: se outra binding já usa a mesma combinação
    const incomingSerialized = serializeKeyCombo(binding.keys);
    const all = Object.entries(state).flatMap(([toolId, actions]) =>
      Object.entries(actions).map(([actionId, keys]) => ({ toolId, actionId, keys }))
    );
    const conflictExisting = all.find(b => serializeKeyCombo(b.keys) === incomingSerialized && (b.toolId !== binding.toolId || b.actionId !== binding.actionId));

    if (conflictExisting) {
      // Ainda assim, aplicamos — cabe à UI alertar e/ou confirmar
      dispatch({ type: 'set', toolId: binding.toolId, actionId: binding.actionId, keys: binding.keys });
      // Disparar evento para sincronização com backend
      window.dispatchEvent(new CustomEvent('shortcuts:changed'));
      return { conflict: { existing: conflictExisting as ShortcutBinding, incoming: binding } };
    }
    dispatch({ type: 'set', toolId: binding.toolId, actionId: binding.actionId, keys: binding.keys });
    // Disparar evento para sincronização com backend
    window.dispatchEvent(new CustomEvent('shortcuts:changed'));
    return {};
  }, [state]);

  const removeShortcut = useCallback((toolId: ToolId, actionId: ActionId) => {
    dispatch({ type: 'remove', toolId, actionId });
    // Disparar evento para sincronização com backend
    window.dispatchEvent(new CustomEvent('shortcuts:changed'));
  }, []);

  const listBindings = useCallback((): ShortcutBinding[] => {
    return Object.entries(state).flatMap(([toolId, actions]) =>
      Object.entries(actions).map(([actionId, keys]) => ({ toolId, actionId, keys }))
    );
  }, [state]);

  const registerExecutor = useCallback((toolId: ToolId, actionId: ActionId, executor: Executor) => {
    executorsRef.current.set(makeKey(toolId, actionId), executor);
  }, []);

  const unregisterExecutor = useCallback((toolId: ToolId, actionId: ActionId) => {
    executorsRef.current.delete(makeKey(toolId, actionId));
  }, []);

  const execute = useCallback((toolId: ToolId, actionId: ActionId, context?: unknown) => {
    const fn = executorsRef.current.get(makeKey(toolId, actionId));
    if (fn) {
      try { fn(context); } catch (err) { logger.error('[shortcuts] executor error', err); }
    }
  }, []);

  const findBySerializedCombo = useCallback((serialized: string): ShortcutBinding[] => {
    const all = listBindings();
    return all.filter(b => serializeKeyCombo(b.keys) === serialized);
  }, [listBindings]);

  /**
   * Carrega atalhos de fonte externa (API) sem persistir em localStorage
   * Usado para carregar os defaults do backend
   */
  const hydrate = useCallback((newState: ShortcutState) => {
    const toolsCount = Object.keys(newState).length;
    const bindingsCount = Object.values(newState).reduce((sum, actions) => sum + Object.keys(actions).length, 0);
    
    dispatch({ type: 'hydrate', state: newState });
    setIsHydrated(true);
    
    logger.info('[SHORTCUTS] Atalhos hidratados da API', { 
      tools: toolsCount,
      bindings: bindingsCount
    });
  }, []);

  const value = useMemo<KeyboardContextValue>(() => ({
    state,
    setShortcut,
    removeShortcut,
    listBindings,
    registerExecutor,
    unregisterExecutor,
    execute,
    findBySerializedCombo,
    hydrate,
    isHydrated,
  }), [state, setShortcut, removeShortcut, listBindings, registerExecutor, unregisterExecutor, execute, findBySerializedCombo, hydrate, isHydrated]);

  return (
    <KeyboardContext.Provider value={value}>{children}</KeyboardContext.Provider>
  );
};

export function useKeyboardContext(): KeyboardContextValue {
  const ctx = useContext(KeyboardContext);
  if (!ctx) throw new Error('useKeyboardContext must be used within KeyboardProvider');
  return ctx;
}

// Helper para checar se o elemento focado é campo de texto
export function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  const editable = (el as any).isContentEditable;
  return editable || tag === 'input' || tag === 'textarea' || tag === 'select';
}


